import type { ReactNode } from 'react';

/** Render a title string, brand-colouring any `*keyword*` spans. */
function highlight(title: string): ReactNode {
  const parts = title.split(/(\*[^*]+\*)/g);
  return parts.map((part, i) =>
    part.startsWith('*') && part.endsWith('*') ? (
      <span key={i} className="text-brand">
        {part.slice(1, -1)}
      </span>
    ) : (
      part
    ),
  );
}

interface StepFrameProps {
  /** 1-based step number, e.g. 3. Omitted in compact/edit mode. */
  number?: number;
  required: boolean;
  /** Question string; wrap the keyword in *asterisks* to brand-highlight it. */
  title: string;
  description?: string;
  requiredLabel: string;
  optionalLabel: string;
  children: ReactNode;
  /** Compact = the single-screen edit layout; false = the wizard step. */
  compact?: boolean;
}

export function StepFrame({
  number,
  required,
  title,
  description,
  requiredLabel,
  optionalLabel,
  children,
  compact,
}: StepFrameProps) {
  if (compact) {
    return (
      <div className="border-b border-gray-100 py-5 last:border-b-0">
        <div className="mb-3 text-base font-semibold text-gray-800">
          {highlight(title)}
        </div>
        {description && (
          <p className="mb-3 mt-0 text-sm text-gray-400">{description}</p>
        )}
        {children}
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-xl">
      <div className="mb-2 flex items-center gap-3 text-xs font-medium">
        {number != null && (
          <span className="text-brand">{String(number).padStart(2, '0')}</span>
        )}
        <span className="h-px w-6 bg-gray-200" />
        <span
          className={
            required
              ? 'rounded-full bg-red-50 px-2 py-0.5 uppercase tracking-wide text-red-500'
              : 'uppercase tracking-wide text-gray-400'
          }
        >
          {required ? requiredLabel : optionalLabel}
        </span>
      </div>
      <h2 className="m-0 text-3xl font-semibold leading-tight text-gray-900">
        {highlight(title)}
      </h2>
      {description && (
        <p className="mb-6 mt-2 text-sm text-gray-400">{description}</p>
      )}
      <div className="mt-6">{children}</div>
    </div>
  );
}
